export const REFERRAL_ADDON_ID = 'sense-referral-row';

export const isReferralLink = (variable: string): boolean => {
  return (
    typeof variable === 'string' &&
    (variable.includes('referral_trigger') ||
      variable.includes('.response.referral_link'))
  );
};

export type ReferralMetadata = {
  link_type: 'program' | 'dynamic_identifier';
  select_identifier_label?: string;
  select_identifier_value?: string;
  program_id?: string;
  program_link_type?: 'company' | 'owner';
};
export const LINK_TYPE_PROGRAM = 'program';
export const LINK_TYPE_IDENTIFIER = 'identifier';
export const ITEMS_PER_PAGE = 10;

export type LinkType = 'program' | 'identifier';

const buildProgramParams = (
  metadata: ReferralMetadata,
): Record<string, unknown> => {
  return {
    ...(metadata.program_id && {program_id: metadata.program_id}),
    ...(metadata.program_link_type && {
      program_link_type: metadata.program_link_type,
    }),
    ...(metadata.program_link_type === 'owner' &&
      metadata.select_identifier_label && {
        select_identifier_label: metadata.select_identifier_label,
      }),
    ...(metadata.program_link_type === 'owner' &&
      metadata.select_identifier_value && {
        select_identifier_value: metadata.select_identifier_value,
      }),
  };
};

const buildDynamicIdentifierParams = (
  metadata: ReferralMetadata,
): Record<string, unknown> => {
  return {
    ...(metadata.select_identifier_label && {
      select_identifier_label: metadata.select_identifier_label,
    }),
    ...(metadata.select_identifier_value && {
      select_identifier_value: metadata.select_identifier_value,
    }),
  };
};

export const buildReferralTriggerParams = (
  metadata: ReferralMetadata,
): Record<string, unknown> => {
  const baseParams = {
    link_type: metadata.link_type,
  };

  const typeSpecificParams =
    metadata.link_type === 'program'
      ? buildProgramParams(metadata)
      : buildDynamicIdentifierParams(metadata);

  return {
    ...baseParams,
    ...typeSpecificParams,
  };
};
export type ReferralType = 'button' | 'text';

export type CompanyLinkPreviewDetails = {
  preview_details?: {
    referral_link?: string;
  }[];
};

export type OwnerLinkPreviewDetails = {
  preview_details?: Array<
    {
      referral_link: string;
      email_id?: string;
    } & Record<string, string | undefined>
  >;
};

export type DynamicProgramPreviewDetails = {
  preview_details?: Array<
    {
      referral_program_name: string;
      referral_program_id: string;
      referral_link: string;
    } & Record<string, string | undefined>
  >;
};

export type ProgramLinkType = 'companyLink' | 'ownerLink';
export type LinkTypeState = 'program' | 'identifier';

export const getInitialState = (
  initialMetadata?: ReferralMetadata | null,
): {
  linkType: LinkTypeState;
  selectedProgramLinkType: ProgramLinkType | null;
  selectedProgramId: string | null;
  selectedOwnerVariable: string | null;
  selectedIdentifier: string | null;
} => {
  if (!initialMetadata) {
    return {
      linkType: 'program',
      selectedProgramLinkType: null,
      selectedProgramId: null,
      selectedOwnerVariable: null,
      selectedIdentifier: null,
    };
  }

  const isProgramType = initialMetadata.link_type === 'program';
  const isDynamicIdentifierType =
    initialMetadata.link_type === 'dynamic_identifier';

  const linkType: LinkTypeState = isDynamicIdentifierType
    ? 'identifier'
    : 'program';

  // Program type state
  let selectedProgramLinkType: ProgramLinkType | null = null;
  let selectedProgramId: string | null = null;
  let selectedOwnerVariable: string | null = null;

  if (isProgramType) {
    if (initialMetadata.program_id) {
      selectedProgramId = initialMetadata.program_id;
    }

    if (initialMetadata.program_link_type) {
      selectedProgramLinkType =
        initialMetadata.program_link_type === 'owner'
          ? 'ownerLink'
          : 'companyLink';

      if (
        initialMetadata.program_link_type === 'owner' &&
        initialMetadata.select_identifier_value
      ) {
        selectedOwnerVariable = initialMetadata.select_identifier_value;
      }
    }
  }

  // Dynamic identifier type state
  const selectedIdentifier: string | null = isDynamicIdentifierType
    ? initialMetadata.select_identifier_value ?? null
    : null;

  return {
    linkType,
    selectedProgramLinkType,
    selectedProgramId,
    selectedOwnerVariable,
    selectedIdentifier,
  };
};

export function getReferralBeefreeJson(
  componentType: string,
  response_key: string,
  metadata: ReferralMetadata,
) {
  const placeholder = `{{$.${response_key}.response.referral_link}}`;

  let modules: Array<{
    type: string;
    text?: string;
    html?: string;
    href?: string;
  }> = [];

  if (componentType === 'button') {
    modules = [
      {
        type: 'button',
        text: 'Referral Link',
        href: placeholder,
      },
    ];
  } else {
    modules = [
      {
        type: 'paragraph',
        html: `<a href="${placeholder}">Referral Link</a>`,
      } as {
        type: string;
        html: string;
      },
    ];
  }

  const metadataPayload = buildReferralTriggerParams(metadata);

  return {
    type: 'rowAddon',
    value: {
      name: 'referralRowAddon',
      columns: [
        {
          weight: 12,
          modules,
        },
      ],
      metadata: {
        ...metadataPayload,
        referralType: componentType,
      },
    },
  };
}
